import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { UnreadProvider, useUnread } from '../lib/unread';
import { OfflineProvider, useOffline } from '../lib/offlineContext';
import { UpdatePrompt } from '../pwa/register';

/**
 * The mobile shell: a header and a bottom navigation bar.
 *
 * Four destinations, because five is where a thumb starts missing on a 360px
 * screen. Notifications live in the header rather than the bar — they are a
 * check, not a place you go.
 */

// Inline SVG rather than Unicode glyphs, which render differently per
// handset and are missing from some Android fonts.
const ICONS: Record<string, string> = {
  home: 'M3 11l9-8 9 8v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z',
  checkup: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zm0 4v5l3 3',
  reports: 'M6 3h9l4 4v14H6zM14 3v5h5M9 13h6M9 17h6',
  fitness: 'M20 6L9 17l-5-5',
  bell: 'M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0',
  user: 'M20 21a8 8 0 0 0-16 0M12 13a5 5 0 1 0 0-10 5 5 0 0 0 0 10z',
};
function Icon({ name, className = 'h-5 w-5' }: { name: string; className?: string }) {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d={ICONS[name]} />
    </svg>
  );
}

const NAV = [
  { to: '/home', label: 'Home', icon: 'home' },
  { to: '/examination', label: 'Checkup', icon: 'checkup' },
  { to: '/records', label: 'Reports', icon: 'reports' },
  { to: '/fitness', label: 'Fitness', icon: 'fitness' },
];

export function AppShell() {
  const { employee } = useAuth();
  // The store opens once per session, keyed by who is signed in — opening is
  // what enforces the shared-handset wipe, so it happens at the one point
  // identity is known and not per screen.
  return (
    <OfflineProvider employeeId={employee!.id}>
      <UnreadProvider>
        <Shell />
      </UnreadProvider>
    </OfflineProvider>
  );
}

function Shell() {
  const { employee } = useAuth();
  const nav = useNavigate();
  const { count } = useUnread();
  const { online } = useOffline();

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col bg-white">
      <header className="sticky top-0 z-10 flex items-center gap-2 border-b border-neutral-200 bg-white px-2 py-2">
        {/* The worker's own name opens their details. /profile — the address,
            the theme and, above all, Sign out — had no way in: on a shared
            handset the next person picked up a phone still signed in to
            somebody else's medical record, with nothing on screen to end it. */}
        <Link
          to="/profile"
          className="flex min-h-11 min-w-0 flex-1 items-center gap-2.5 rounded-lg px-2 py-1 active:bg-neutral-100"
        >
          <span aria-hidden className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-700">
            <Icon name="user" className="h-5 w-5" />
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-neutral-900">{employee?.fullName}</span>
            <span className="block font-mono text-xs text-neutral-600">{employee?.employeeCode}</span>
            <span className="sr-only">Your details and sign out</span>
          </span>
        </Link>
        <button
          onClick={() => nav('/notifications')}
          aria-label={count ? `Notifications, ${count} unread` : 'Notifications'}
          className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-lg text-neutral-700 active:bg-neutral-100"
        >
          <Icon name="bell" />
          {count > 0 && (
            <span aria-hidden className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger-600 px-1 text-[10px] font-semibold text-white">
              {count > 9 ? '9+' : count}
            </span>
          )}
        </button>
      </header>

      {/* Said once, at the top, rather than repeated on every card. A worker
          who knows they are offline can read a saved copy for what it is. */}
      {!online && (
        <p
          role="status"
          className="border-b border-neutral-200 bg-neutral-100 px-4 py-2 text-center text-xs text-neutral-700"
        >
          You are offline. You can still see what was saved on this phone.
        </p>
      )}

      {/* Bottom padding clears the nav bar plus the iOS home indicator. */}
      <main className="flex-1 px-4 pb-28 pt-4">
        <Outlet />
      </main>

      <UpdatePrompt />

      <nav
        aria-label="Main"
        className="fixed inset-x-0 bottom-0 mx-auto max-w-md border-t border-neutral-200 bg-white pb-[env(safe-area-inset-bottom)]"
      >
        <ul className="flex">
          {NAV.map((item) => (
            <li key={item.to} className="flex-1">
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  // 56px tall: a comfortable tap target for a gloved or dry
                  // hand, which is what this workforce has.
                  `flex h-14 flex-col items-center justify-center gap-0.5 text-xs ${
                    isActive ? 'font-semibold text-primary-700' : 'text-neutral-600'
                  }`
                }
              >
                <Icon name={item.icon} />
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
